import React, { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Printer, FileDown, Copy, MapPin, QrCode } from "lucide-react";
import { toast } from "sonner";
import { PageHeader } from "../../components/common";
import { FetchButton, FetchDirtyHint, FetchPrompt, useFetchGate } from "../../components/FetchBar";
import { TerritoryFilterSelects, useTerritoryMasters, territoryParams, territoryLabel, EMPTY_TERRITORY } from "../../components/TerritoryFilter";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Skeleton } from "../../components/ui/skeleton";
import { api, errMsg, fmtINR, downloadBlob } from "../../lib/api";

const STATUS_OPTIONS = [
  { value: "all", label: "All demands" },
  { value: "paid", label: "Paid demands" },
  { value: "unpaid", label: "Unpaid demands" },
];

const Stat = ({ label, value, testId }) => (
  <div>
    <p className="text-xs text-muted-foreground">{label}</p>
    <p className="text-lg font-semibold font-heading tabular-nums" data-testid={testId}>{value}</p>
  </div>
);

export default function DemandPrint() {
  const { code } = useParams();
  const [status, setStatus] = useState("all");
  // Phase 176: Zone / Ward / Mohalla are multi-select; each field is a comma-joined id list ("" = all)
  const [territory, setTerritory] = useState(EMPTY_TERRITORY);
  const masters = useTerritoryMasters(code);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);

  // Phase 133: the summary (and the PDF) use the filters applied when Fetch was clicked
  const gate = useFetchGate({ status, zoneId: territory.zoneId, wardId: territory.wardId, mohallaId: territory.mohallaId });
  const params = useCallback(() => {
    const a = gate.applied || {};
    return { status: a.status || status, ...territoryParams(a) };
  }, [gate.appliedKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!gate.fetched) return;
    setLoading(true);
    api.get(`/t/${code}/demand-print/summary`, { params: params() })
      .then((r) => setSummary(r.data))
      .catch((e) => { setSummary(null); toast.error(errMsg(e)); })
      .finally(() => setLoading(false));
  }, [code, params, gate.fetched, gate.version]);

  const download = async () => {
    setDownloading(true);
    try {
      const qs = new URLSearchParams({ ...params(), origin: window.location.origin }).toString();   // Phase 208: QR base URL
      await downloadBlob(`/t/${code}/demand-print/pdf?${qs}`, `demand-slips-${params().status}.pdf`);
      toast.success("Demand slips PDF downloaded — each copy carries a PIN QR code for online payment");
    } catch (e) { toast.error(errMsg(e)); }
    finally { setDownloading(false); }
  };

  const appliedLabel = territoryLabel(masters, gate.applied || {});

  return (
    <div>
      <PageHeader title="Demand Print" subtitle="Generate printable demand slips — every property page carries an Agent Copy and a Customer Copy" />

      <Card className="shadow-sm mb-4">
        <CardContent className="p-4">
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-3">
            <div className="space-y-1">
              <Label className="text-xs">Demand status</Label>
              <Select value={status} onValueChange={(v) => v && setStatus(v)}>
                <SelectTrigger className="h-10" data-testid="dp-status-select">
                  <SelectValue>{STATUS_OPTIONS.find((s) => s.value === status)?.label}</SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {STATUS_OPTIONS.map((s) => (
                    <SelectItem key={s.value} value={s.value} data-testid={`dp-status-${s.value}`}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="lg:col-span-3">
              <TerritoryFilterSelects masters={masters} value={territory} onChange={setTerritory} testIdPrefix="dp" />
            </div>
          </div>
          <div className="mt-3 flex items-center gap-3 flex-wrap">
            <FetchButton onClick={gate.fetch} loading={loading} dirty={gate.dirty} fetched={gate.fetched} testId="dp-fetch-button" />
            <FetchDirtyHint dirty={gate.dirty} testId="dp-fetch-dirty" />
            {!gate.fetched && <span className="text-xs text-muted-foreground">Pick the demand status and area, then click Fetch to count the matching slips.</span>}
          </div>
        </CardContent>
      </Card>

      {!gate.fetched ? (
        <Card className="shadow-sm"><CardContent className="p-0">
          <FetchPrompt onFetch={gate.fetch} testId="dp-fetch-prompt" compact
            subtitle="Choose the demand status and the zone / ward / mohalla above, then click Fetch to see how many slips will be printed." />
        </CardContent></Card>
      ) : loading && !summary ? (
        <Skeleton className="h-32 rounded-lg" />
      ) : summary && (
        <Card className="shadow-sm" data-testid="dp-summary-card">
          <CardContent className="p-4 space-y-4">
            {appliedLabel && (
              <p className="text-xs text-muted-foreground flex items-center gap-1.5" data-testid="dp-area-label">
                <MapPin className="h-3.5 w-3.5" /> Area: <span className="font-medium text-foreground">{appliedLabel}</span>
              </p>
            )}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <Stat label="Properties" value={summary.properties} testId="dp-count-properties" />
              <Stat label="Demand lines" value={summary.demands} testId="dp-count-demands" />
              <Stat label="Total demand" value={fmtINR(summary.total_demand)} testId="dp-total-demand" />
              <Stat label="Outstanding balance" value={fmtINR(summary.total_balance)} testId="dp-total-balance" />
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <Button onClick={download} disabled={downloading || summary.properties === 0 || !summary.within_limit}
                data-testid="dp-download-button">
                <FileDown className="h-4 w-4 mr-2" />
                {downloading ? "Preparing PDF..." : "Download demand slips PDF"}
              </Button>
              <span className="text-xs text-muted-foreground flex items-center gap-1.5">
                <Copy className="h-3.5 w-3.5" /> 2 copies per property — Agent Copy + Customer Copy with a cut line
              </span>
            </div>
            {summary.properties === 0 && (
              <p className="text-sm text-muted-foreground" data-testid="dp-empty-note">No demands match the selected filters.</p>
            )}
            {!summary.within_limit && (
              <p className="text-sm text-amber-700" data-testid="dp-limit-note">
                {summary.properties} properties match — a single PDF is limited to {summary.max_properties_per_pdf}.
                Narrow down using zone, ward or mohalla filters.
              </p>
            )}
            <p className="text-xs text-muted-foreground flex items-center gap-1.5">
              <Printer className="h-3.5 w-3.5" /> Print on A4 — each page holds both copies of one property's demand slip.
            </p>
            <p className="text-xs text-muted-foreground flex items-center gap-1.5" data-testid="dp-qr-note">
              <QrCode className="h-3.5 w-3.5" /> Every copy carries a PIN QR code — citizens scan it to open their property on the payment portal and pay online.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
